import { apiClient, identityClient, buildQueryParams } from './client';
import {
  DashboardViewModel,
  Customer,
  FullCustomerViewModel,
  Provider,
  FullProviderViewModel,
  Order,
  OrderOffer,
  OrderNote,
  OrderAction,
  OrderChat,
  OrderPurchase,
  OrderOfferStatistics,
  Attachment,
  Location,
  BalanceTransaction,
  UserNote,
  Gallery,
  Service,
  Product,
  ProductInput,
  ProductsSummary,
  BookingSlot,
  BookingSlotInput,
  Coupon,
  CouponDetail,
  Offer,
  OfferStatus,
  Ticket,
  TicketMessage,
  TicketCategory,
  TicketSubCategory,
  TicketStatus,
  TicketInputStatus,
  SupportProfile,
  UserType,
  NotificationMethodType,
  NotificationType,
  AttachmentType,
  BroadcastNotification,
  Notification,
  Address,
  Tenant,
  TenantListItem,
  TenantModule,
  CreateTenantRequest,
  UpdateTenantRequest,
  SetTenantModulesRequest,
  AvailableModule,
  CurrentTenantMeta,
  AppVersion,
  UpdateAppVersionRequest,
  TenantBranding,
  PlatformBranding,
  SliderItem,
  CreateSliderRequest,
  UpdateSliderRequest,
  Faq,
  FaqType,
  Quotation,
  QuotationOffer,
  QuotationNote,
  PaginatedResponse,
  ConsultationSession,
  NearbyOrder,
  NearbyProvider,
  ProviderEntityConfig,
  TeamMemberViewModel,
  Violation,
  Penalty,
  ProviderPersonalProfileInput,
} from '@/types';

// Dashboard API
export const dashboardApi = {
  getStatistics: async (date: string): Promise<DashboardViewModel> => {
    const response = await apiClient.get<DashboardViewModel>(`/supports/dashboard?date=${date}`);
    return response.data;
  },
};

// Customers API
export const customersApi = {
  getAll: async (statusId: number, page: number, keyword?: string): Promise<Customer[]> => {
    // Backend endpoint: GET /supports/customers with statusId, Page, keyword params
    const params = buildQueryParams({ statusId, Page: page, keyword });
    const response = await apiClient.get<Customer[]>(`/supports/customers${params}`);
    return response.data;
  },

  getById: async (userId: string): Promise<FullCustomerViewModel> => {
    const response = await apiClient.get<FullCustomerViewModel>(`/supports/customers/${userId}`);
    return response.data;
  },

  updateStatus: async (
    customerUserId: string,
    statusId: number,
    supportMessage: string
  ): Promise<boolean> => {
    const response = await apiClient.post<boolean>('/supports/customers/status', null, {
      params: { customerUserId, statusId, supportMessage },
    });
    return response.data;
  },

  updateProfile: async (
    userId: string,
    data: Partial<Customer>
  ): Promise<Customer> => {
    const response = await apiClient.post<Customer>(`/supports/customers/profile/${userId}`, data);
    return response.data;
  },

  updateProfileImage: async (userId: string, filePath: string): Promise<{ filePath: string }> => {
    const response = await apiClient.post<{ filePath: string }>(
      `/supports/customers/profile/${userId}/image`,
      null,
      { params: { filePath } }
    );
    return response.data;
  },

  getFavorites: async (userId: string, page: number): Promise<Provider[]> => {
    const params = buildQueryParams({ page });
    const response = await apiClient.get<Provider[]>(`/supports/customers/${userId}/favorites${params}`);
    return response.data;
  },

  // Remove favorite provider through the admin facade.
  removeFavorite: async (userId: string, providerId: string): Promise<boolean> => {
    const response = await apiClient.delete<boolean>(`/supports/customers/${userId}/favorites/${providerId}`);
    return response.data;
  },
};

// Providers API
export const providersApi = {
  getAll: async (statusId: number, page: number, keyword?: string): Promise<Provider[]> => {
    // Backend endpoint: GET /supports/providers with statusId, Page, keyword params
    const params = buildQueryParams({ statusId, Page: page, keyword });
    const response = await apiClient.get<Provider[]>(`/supports/providers${params}`);
    return response.data;
  },

  getById: async (providerId: string): Promise<FullProviderViewModel> => {
    const response = await apiClient.get<FullProviderViewModel>(`/supports/providers/${providerId}`);
    return response.data;
  },

  getByDebts: async (page: number): Promise<Provider[]> => {
    const params = buildQueryParams({ page });
    const response = await apiClient.get<Provider[]>(`/supports/providers/debts${params}`);
    return response.data;
  },

  getBySpecialityForOrder: async (
    orderId: number,
    keyword: string,
    page: number
  ): Promise<Provider[]> => {
    const params = buildQueryParams({ keyword, page });
    const response = await apiClient.get<Provider[]>(
      `/supports/providers/speciality/${orderId}${params}`
    );
    return response.data;
  },

  updateStatus: async (
    providerUserId: string,
    statusId: number,
    supportMessage: string
  ): Promise<boolean> => {
    const response = await apiClient.post<boolean>('/supports/providers/status', null, {
      params: { providerUserId, statusId, supportMessage },
    });
    return response.data;
  },

  updateProfile: async (data: ProviderPersonalProfileInput): Promise<Provider> => {
    const response = await apiClient.post<Provider>('/supports/providers/profile', data);
    return response.data;
  },

  updateEntityConfig: async (data: ProviderEntityConfig): Promise<boolean> => {
    const response = await apiClient.post<boolean>('/supports/providers/profile', data);
    return response.data;
  },

  updateProfileImage: async (userId: string, filePath: string): Promise<{ filePath: string }> => {
    const response = await apiClient.post<{ filePath: string }>(
      '/supports/providers/profile/image',
      null,
      { params: { filePath, userId } }
    );
    return response.data;
  },

  // Provider Locations
  getLocations: async (userId: string, page: number): Promise<Location[]> => {
    const params = buildQueryParams({ userId, page });
    const response = await apiClient.get<Location[]>(`/supports/providers/locations${params}`);
    return response.data;
  },

  addLocation: async (data: Partial<Location>): Promise<Location> => {
    const response = await apiClient.post<Location>('/supports/providers/locations', data);
    return response.data;
  },

  deleteLocation: async (userId: string, specialtyId: number): Promise<boolean> => {
    const response = await apiClient.delete<boolean>('/supports/providers/locations', {
      params: { userId, specialtyId },
    });
    return response.data;
  },

  // Provider Galleries
  getGalleries: async (userId: string, specialtyId: number, page: number): Promise<Gallery[]> => {
    const params = buildQueryParams({ userId, specialtyId, page });
    const response = await apiClient.get<Gallery[]>(`/supports/providers/galleries${params}`);
    return response.data;
  },

  addGallery: async (userId: string, data: Partial<Gallery>): Promise<Gallery> => {
    const response = await apiClient.post<Gallery>(`/supports/providers/galleries`, data, {
      params: { userId },
    });
    return response.data;
  },

  updateGallery: async (
    userId: string,
    galleryId: number,
    data: { title: string; description?: string }
  ): Promise<Gallery> => {
    // Backend uses same endpoint for add/update, with ProviderGalleryId indicating update
    const response = await apiClient.post<Gallery>(
      `/supports/providers/galleries`,
      { ...data, ProviderGalleryId: galleryId },
      { params: { userId } }
    );
    return response.data;
  },

  deleteGallery: async (userId: string, galleryId: number): Promise<boolean> => {
    const response = await apiClient.delete<boolean>(`/supports/providers/galleries/${galleryId}`, {
      params: { userId },
    });
    return response.data;
  },

  // Provider Services
  getServices: async (
    userId: string,
    subspecailtyId: number,
    page: number
  ): Promise<Service[]> => {
    const params = buildQueryParams({ userId, subspecailtyId, page });
    const response = await apiClient.get<Service[]>(`/supports/providers/services${params}`);
    return response.data;
  },

  addService: async (data: Partial<Service>): Promise<boolean> => {
    const response = await apiClient.post<boolean>('/supports/providers/services', data);
    return response.data;
  },

  deleteService: async (userId: string, serviceId: number): Promise<boolean> => {
    const response = await apiClient.delete<boolean>(`/supports/providers/services/${serviceId}`, {
      params: { userId },
    });
    return response.data;
  },

  // Register new provider (via identity API)
  register: async (data: {
    fullName: string;
    mobileNo: string;
    genderId: number;
    nationalityId: number;
    birthDate?: string;
    bio?: string;
    cityId?: number;
    districtId?: number;
  }): Promise<Provider> => {
    // Identity's manual-registration contract accepts Name/Mobile only.
    const response = await identityClient.post<Provider>('/supports/providers', {
      name: data.fullName,
      mobile: data.mobileNo,
    });
    return response.data;
  },

  // ═══ Phase 1: Team Management (read-only from tenant admin) ═══
  getProviderTeamMembers: async (providerUserId: string): Promise<TeamMemberViewModel[]> => {
    const response = await apiClient.get<TeamMemberViewModel[]>(`/providers/team?userId=${providerUserId}`);
    return response.data;
  },
};

// Orders API
export const ordersApi = {
  getAll: async (combinedStatusId: number, page: number, keyword?: string): Promise<Order[]> => {
    const params = buildQueryParams({ combinedStatusId, page, keyword });
    const response = await apiClient.get<Order[]>(`/supports/orders${params}`);
    return response.data;
  },

  getByProvider: async (
    userId: string,
    combinedStatusId: number,
    page: number
  ): Promise<Order[]> => {
    const params = buildQueryParams({ userId, combinedStatusId, page });
    const response = await apiClient.get<Order[]>(`/supports/orders/providers${params}`);
    return response.data;
  },

  getByCustomer: async (
    userId: string,
    combinedStatusId: number,
    page: number
  ): Promise<Order[]> => {
    const params = buildQueryParams({ userId, combinedStatusId, page });
    const response = await apiClient.get<Order[]>(`/supports/orders/customers${params}`);
    return response.data;
  },

  getByQuality: async (orderQualityId: number, page: number): Promise<Order[]> => {
    const params = buildQueryParams({ orderQualityId, page });
    const response = await apiClient.get<Order[]>(`/supports/orders/quality${params}`);
    return response.data;
  },

  getById: async (orderId: number): Promise<Order> => {
    const response = await apiClient.get<Order>(`/supports/orders/${orderId}`);
    return response.data;
  },

  getConsultationSession: async (orderId: number): Promise<ConsultationSession> => {
    const response = await apiClient.get<ConsultationSession>(`/consultations/orders/${orderId}/session`);
    return response.data;
  },

  updateStatus: async (
    orderId: number,
    data: {
      orderUpdateTypeEnum: number;
      newStatusId: number;
      reasonBody?: string;
      agreedTimeOn?: string;
    }
  ): Promise<void> => {
    await apiClient.post(`/supports/orders/${orderId}/status`, data);
  },

  updateExecution: async (
    orderId: number,
    data: { price: number; isAgreed: boolean }
  ): Promise<Order> => {
    const response = await apiClient.post<Order>(`/supports/orders/${orderId}/execution`, data);
    return response.data;
  },

  updateQuality: async (
    orderId: number,
    data: { orderQualityId: number; note?: string }
  ): Promise<boolean> => {
    const response = await apiClient.post<boolean>(`/supports/orders/${orderId}/quality`, data);
    return response.data;
  },

  addToDangerZone: async (orderId: number, note: string): Promise<boolean> => {
    const response = await apiClient.post<boolean>(
      `/supports/orders/${orderId}/dangermode`,
      null,
      { params: { note } }
    );
    return response.data;
  },

  removeFromDangerZone: async (orderId: number): Promise<boolean> => {
    const response = await apiClient.post<boolean>(`/supports/orders/${orderId}/removedangermode`);
    return response.data;
  },

  updateBody: async (orderId: number, data: { body: string }): Promise<boolean> => {
    const response = await apiClient.post<boolean>(`/supports/orders/${orderId}/body`, data);
    return response.data;
  },

  updateSpecialty: async (orderId: number, specialtyId: number): Promise<boolean> => {
    const response = await apiClient.post<boolean>(
      `/supports/orders/${orderId}/specialty`,
      null,
      { params: { specialtyId } }
    );
    return response.data;
  },

  // Order Notes
  getNotes: async (orderId: number, page: number): Promise<OrderNote[]> => {
    const params = buildQueryParams({ page });
    const response = await apiClient.get<OrderNote[]>(`/supports/orders/${orderId}/notes${params}`);
    return response.data;
  },

  addNote: async (orderId: number, data: { note: string }): Promise<OrderNote> => {
    const response = await apiClient.post<OrderNote>(`/supports/orders/${orderId}/notes`, data);
    return response.data;
  },

  // Order Actions
  getActions: async (orderId: number, page: number): Promise<OrderAction[]> => {
    const params = buildQueryParams({ page });
    const response = await apiClient.get<OrderAction[]>(
      `/supports/orders/${orderId}/actions${params}`
    );
    return response.data;
  },

  // Order Offers
  getOffers: async (orderId: number, page: number): Promise<OrderOffer[]> => {
    const params = buildQueryParams({ page });
    const response = await apiClient.get<OrderOffer[]>(`/supports/orders/${orderId}/offers${params}`);
    return response.data;
  },

  addOffer: async (orderId: number, data: Partial<OrderOffer>): Promise<OrderOffer> => {
    const response = await apiClient.post<OrderOffer>(`/supports/orders/${orderId}/offers`, data);
    return response.data;
  },

  acceptOffer: async (orderId: number, offerId: number, userId: string): Promise<Order> => {
    const response = await apiClient.post<Order>(
      `/supports/orders/${orderId}/offers/${offerId}`,
      null,
      { params: { userId } }
    );
    return response.data;
  },

  removeOffer: async (orderId: number, offerId: number): Promise<OrderOffer> => {
    const response = await apiClient.delete<OrderOffer>(
      `/supports/orders/${orderId}/offers/${offerId}`
    );
    return response.data;
  },

  // Order Chat
  getChat: async (orderId: number, page: number): Promise<OrderChat[]> => {
    const params = buildQueryParams({ page });
    const response = await apiClient.get<OrderChat[]>(`/supports/orders/${orderId}/chat${params}`);
    return response.data;
  },

  addChatMessage: async (
    orderId: number,
    data: { body: string; messageType: number; filePath?: string }
  ): Promise<OrderChat> => {
    const response = await apiClient.post<OrderChat>(`/supports/orders/${orderId}/chat`, data);
    return response.data;
  },

  // Order Purchases
  getPurchases: async (orderId: number, page: number): Promise<OrderPurchase[]> => {
    const params = buildQueryParams({ page });
    const response = await apiClient.get<OrderPurchase[]>(
      `/supports/orders/${orderId}/purchase${params}`
    );
    return response.data;
  },

  addPurchase: async (orderId: number, data: Partial<OrderPurchase>): Promise<number> => {
    const response = await apiClient.post<number>(`/supports/orders/${orderId}/purchase`, data);
    return response.data;
  },

  deletePurchase: async (orderPurchaseId: number): Promise<boolean> => {
    const response = await apiClient.delete<boolean>(`/supports/orders/purchase/${orderPurchaseId}`);
    return response.data;
  },

  // Manual Dispatching
  dispatchToProvider: async (orderId: number, providerUserId: string): Promise<boolean> => {
    const response = await apiClient.post<boolean>(
      `/supports/orders/${orderId}/dispatching/${providerUserId}`
    );
    return response.data;
  },

  dispatchToProviders: async (orderId: number, userIds: string[]): Promise<number> => {
    const response = await apiClient.post<number>(`/supports/orders/${orderId}/dispatching`, {
      orderId,
      userIds,
    });
    return response.data;
  },

  // Get offer statistics
  getOfferStatistics: async (orderId: number): Promise<OrderOfferStatistics> => {
    const response = await apiClient.get<OrderOfferStatistics>(
      `/supports/orders/${orderId}/offers/statistics`
    );
    return response.data;
  },

  // Reopen order
  reopenOrder: async (orderId: number): Promise<boolean> => {
    const response = await apiClient.post<boolean>(`/supports/orders/${orderId}/reopen`);
    return response.data;
  },

  // Update order location
  updateLocation: async (
    orderId: number,
    data: { addressName: string; latitude: number; longitude: number; districtId?: number }
  ): Promise<boolean> => {
    const response = await apiClient.post<boolean>(`/supports/orders/${orderId}/location`, data);
    return response.data;
  },

  // Rate order (for quality tab)
  rateOrder: async (
    orderId: number,
    data: {
      rate: number;
      behaviorRate: number;
      rateComment?: string;
      customerRate?: number;
      customerRateComment?: string;
    }
  ): Promise<boolean> => {
    const response = await apiClient.post<boolean>(`/supports/orders/${orderId}/rate`, data);
    return response.data;
  },

  // Update order items
  updateItems: async (
    orderId: number,
    data: { items: Array<{ itemName: string; quantity: number; price: number }> }
  ): Promise<boolean> => {
    const response = await apiClient.post<boolean>(`/supports/orders/${orderId}/items`, data);
    return response.data;
  },

  // Handle/edit chat message
  handleChatMessage: async (
    chatMessageId: number,
    data: { isHandled: boolean; handledNote?: string }
  ): Promise<boolean> => {
    const response = await apiClient.put<boolean>(`/supports/orders/chat/${chatMessageId}`, data);
    return response.data;
  },

  // Get all comments by date (for monitoring)
  getAllComments: async (onDate: string, page: number): Promise<OrderChat[]> => {
    const params = buildQueryParams({ onDate, page });
    const response = await apiClient.get<OrderChat[]>(`/supports/orders/all_comments${params}`);
    return response.data;
  },

  // Update agreed time
  updateAgreedTime: async (orderId: number, agreedTime: string): Promise<boolean> => {
    const response = await apiClient.post<boolean>(
      `/supports/orders/${orderId}/agreedtime`,
      null,
      { params: { agreedTime } }
    );
    return response.data;
  },

  // Change provider
  changeProvider: async (orderId: number, reason: string): Promise<number> => {
    const response = await apiClient.post<number>(
      `/supports/orders/${orderId}/change_provider`,
      null,
      { params: { reason } }
    );
    return response.data;
  },

  // Update inner status
  updateInnerStatus: async (orderId: number, innerStatusId: number): Promise<boolean> => {
    const response = await apiClient.post<boolean>(
      `/supports/orders/${orderId}/innerstatus`,
      null,
      { params: { innerStatusId } }
    );
    return response.data;
  },
};

// Quotations API
export const quotationsApi = {
  getAll: async (status: number, page: number): Promise<Quotation[]> => {
    const params = buildQueryParams({ status, page });
    const response = await apiClient.get<Quotation[]>(`/supports/quotations${params}`);
    return response.data;
  },

  getById: async (quotationId: number): Promise<Quotation> => {
    const response = await apiClient.get<Quotation>(`/supports/quotations/${quotationId}`);
    return response.data;
  },

  approve: async (quotationId: number): Promise<boolean> => {
    const response = await apiClient.post<boolean>(`/supports/quotations/${quotationId}/approve`);
    return response.data;
  },

  close: async (quotationId: number, reason: string): Promise<boolean> => {
    const response = await apiClient.post<boolean>(
      `/supports/quotations/${quotationId}/close`,
      null,
      { params: { reason } }
    );
    return response.data;
  },

  reopen: async (quotationId: number): Promise<boolean> => {
    const response = await apiClient.post<boolean>(`/supports/quotations/${quotationId}/reopen`);
    return response.data;
  },

  getOffers: async (quotationId: number, page: number): Promise<QuotationOffer[]> => {
    const params = buildQueryParams({ page });
    const response = await apiClient.get<QuotationOffer[]>(
      `/supports/quotations/${quotationId}/offers${params}`
    );
    return response.data;
  },

  getNotes: async (quotationId: number, page: number): Promise<QuotationNote[]> => {
    const params = buildQueryParams({ page });
    const response = await apiClient.get<QuotationNote[]>(
      `/supports/quotations/${quotationId}/notes${params}`
    );
    return response.data;
  },

  addNote: async (data: { quotationId: number; note: string }): Promise<QuotationNote> => {
    const response = await apiClient.post<QuotationNote>('/supports/quotations/note', data);
    return response.data;
  },
};

// Attachments API
export const attachmentsApi = {
  getAll: async (userId: string, page: number): Promise<Attachment[]> => {
    const params = buildQueryParams({ userId, page });
    const response = await apiClient.get<Attachment[]>(`/supports/attachments${params}`);
    return response.data;
  },

  add: async (
    userId: string,
    filePath: string,
    typeId: AttachmentType
  ): Promise<{ filePath: string }> => {
    const response = await apiClient.post<{ filePath: string }>('/supports/attachments', null, {
      params: { userId, filePath, typeId },
    });
    return response.data;
  },

  handle: async (
    id: number,
    data: { isAccepted: boolean; adminMessage?: string }
  ): Promise<boolean> => {
    const response = await apiClient.put<boolean>(`/supports/attachments/${id}`, {
      id,
      isAccepted: data.isAccepted,
      adminMessage: data.adminMessage || '',
      actionByUserId: '', // Will be set by backend from auth token
    });
    return response.data;
  },

  delete: async (attachmentId: number, userId: string): Promise<boolean> => {
    const response = await apiClient.delete<boolean>(`/supports/attachments/${attachmentId}`, {
      params: { userId },
    });
    return response.data;
  },
};

// Transactions API
export const transactionsApi = {
  getAll: async (userId: string, page: number): Promise<BalanceTransaction[]> => {
    const params = buildQueryParams({ userId, page });
    const response = await apiClient.get<BalanceTransaction[]>(`/supports/transactions${params}`);
    return response.data;
  },

  addManual: async (data: {
    operationId: string;
    userId: string;
    balanceTransactionTypeId: number;
    balanceTransactionChannelId: number;
    maksabOfficeId: number;
    description: string;
    amount: number;
    userTypeEnum: number;
    durationDays?: number;
  }): Promise<boolean> => {
    const response = await apiClient.post<boolean>('/supports/transactions/manual', data);
    return response.data;
  },
};

// User Notes API
export const userNotesApi = {
  getAll: async (userId: string, page: number): Promise<UserNote[]> => {
    const params = buildQueryParams({ page });
    const response = await apiClient.get<UserNote[]>(`/supports/notes/${userId}${params}`);
    return response.data;
  },

  add: async (data: Partial<UserNote>): Promise<UserNote> => {
    const response = await apiClient.post<UserNote>('/supports/notes', data);
    return response.data;
  },
};

// Support Profile API
export const supportProfileApi = {
  get: async (): Promise<SupportProfile> => {
    const response = await apiClient.get<SupportProfile>('/supports/profile');
    return response.data;
  },

  update: async (data: Partial<SupportProfile>): Promise<SupportProfile> => {
    const response = await apiClient.post<SupportProfile>('/supports/profile', data);
    return response.data;
  },

  updateImage: async (filePath: string): Promise<{ filePath: string }> => {
    const response = await apiClient.post<{ filePath: string }>('/supports/profile/image', null, {
      params: { filePath },
    });
    return response.data;
  },
};

// Coupons API (uses /coupons endpoint, not /supports/coupons)
export const couponsApi = {
  getByStatus: async (status: number, page: number): Promise<PaginatedResponse<Coupon>> => {
    const params = buildQueryParams({ status, page });
    const response = await apiClient.get<PaginatedResponse<Coupon>>(`/coupons${params}`);
    return response.data;
  },

  getByCode: async (code: string): Promise<CouponDetail> => {
    const params = buildQueryParams({ code });
    const response = await apiClient.get<CouponDetail>(`/coupons/details${params}`);
    return response.data;
  },

  add: async (data: {
    code: string;
    discountAmount: number;
    firstUsingCount: number;
    reusingCount: number;
    title?: string;
    description?: string;
    activatedOn: string;
    expiresOn: string;
    couponTypeId?: number;
    couponScope?: number;
    couponScopeTargetId?: number;
    isPublicListing?: boolean;
  }): Promise<CouponDetail> => {
    const response = await apiClient.post<CouponDetail>('/coupons', {
      ...data,
      couponTypeId: data.couponTypeId ?? 1,
      couponScope: data.couponScope ?? 0,
      couponScopeTargetId: data.couponScopeTargetId ?? 0,
      isPublicListing: data.isPublicListing ?? false,
    });
    return response.data;
  },

  getOrders: async (couponCode: string, page: number): Promise<Order[]> => {
    const params = buildQueryParams({ couponCode, page });
    const response = await apiClient.get<Order[]>(`/supports/coupons/orders${params}`);
    return response.data;
  },
};

// Offers API
interface OfferWireModel {
  id: number;
  specialtyId: number;
  specialtyName?: string;
  name?: string;
  description?: string;
  offerImage?: string;
  price: number;
  originalPrice: number;
  addedOn?: string;
  fromTime: string;
  toTime: string;
  userId?: string;
  offerStatus: number;
  offerStatusName?: string;
}

const mapOffer = (offer: OfferWireModel): Offer => ({
  id: offer.id,
  providerUserId: offer.userId ?? '',
  specialtyId: offer.specialtyId,
  specialtyName: offer.specialtyName,
  title: offer.name ?? '',
  description: offer.description ?? '',
  price: offer.originalPrice,
  discountPrice: offer.price,
  imageUrl: offer.offerImage,
  statusId:
    offer.offerStatus === 2 && new Date(offer.toTime).getTime() < Date.now()
      ? OfferStatus.Expired
      : offer.offerStatus as OfferStatus,
  statusName: offer.offerStatusName ?? '',
  fromTime: offer.fromTime,
  toTime: offer.toTime,
  createdOn: offer.addedOn ?? '',
});

const toLegacyOfferDate = (value?: string): string => {
  if (!value) return '';
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return value;
  const [year, month, day] = value.slice(0, 10).split('-');
  return year && month && day ? `${day}/${month}/${year}` : value;
};

export const offersApi = {
  getByStatus: async (offerStatus: number, page: number): Promise<Offer[]> => {
    // Core uses Published=2 for persisted state but Active=4 as the derived
    // list filter that excludes expired published offers.
    const backendStatus = offerStatus === OfferStatus.Active ? 4 : offerStatus;
    const params = buildQueryParams({ offerStatus: backendStatus, page });
    const response = await apiClient.get<OfferWireModel[]>(`/supports/offers${params}`);
    return response.data.map(mapOffer);
  },

  getByProvider: async (
    userId: string,
    specialtyId: number,
    page: number
  ): Promise<Offer[]> => {
    const params = buildQueryParams({ userId, specialtyId, page });
    const response = await apiClient.get<OfferWireModel[]>(`/supports/offers/provider${params}`);
    return response.data.map(mapOffer);
  },

  getById: async (offerId: number): Promise<Offer> => {
    const response = await apiClient.get<OfferWireModel>(`/supports/offers/${offerId}`);
    return mapOffer(response.data);
  },

  add: async (data: Partial<Offer>): Promise<number> => {
    const response = await apiClient.post<number>('/supports/offers', {
      id: data.id ?? 0,
      specialtyId: data.specialtyId,
      name: data.title,
      description: data.description,
      offerImage: data.imageUrl,
      price: data.discountPrice || data.price,
      originalPrice: data.price,
      fromTimeString: toLegacyOfferDate(data.fromTime),
      toTimeString: toLegacyOfferDate(data.toTime),
      minimumQuantity: 0,
      maximumQuantity: 0,
    });
    return response.data;
  },

  updateStatus: async (offerId: number, data: Partial<Offer>): Promise<boolean> => {
    // The gateway's OfferUpdateModel names this field OfferStatus. Keep the
    // dashboard's statusId model internal and translate it at the API boundary.
    const { statusId, ...rest } = data;
    const payload = statusId === undefined
      ? rest
      : { ...rest, offerStatus: statusId };
    const response = await apiClient.put<boolean>(`/supports/offers/${offerId}`, payload);
    return response.data;
  },

  delete: async (offerId: number): Promise<boolean> => {
    const response = await apiClient.delete<boolean>(`/supports/offers/${offerId}`);
    return response.data;
  },

  getOrders: async (offerId: number, page: number): Promise<Order[]> => {
    const params = buildQueryParams({ page });
    const response = await apiClient.get<Order[]>(`/supports/offers/${offerId}/orders${params}`);
    return response.data;
  },
};

// Notifications API
export const notificationsApi = {
  // Get broadcast history with analytics
  getBroadcasts: async (userType: number, page: number): Promise<BroadcastNotification[]> => {
    const params = buildQueryParams({ userType, page });
    const response = await apiClient.get<BroadcastNotification[]>(
      `/adminnotifications/broadcast${params}`
    );
    return response.data;
  },

  // Send broadcast notification.
  // Backend NotificationBroadCastInputModel is TOPIC-based: it needs the
  // FCM topic(s), notificationTypeId in the CAST range (27) and a delivery
  // method — a bare {title, message, userType} body is rejected with
  // InvalidNotificationType. The Gateway interprets the exact audience
  // selectors "customers" and "providers"; any other value means everyone.
  sendBroadcast: async (data: {
    title: string;
    message: string;
    /** UserType.Customer / UserType.Provider, or 0 = everyone */
    userType: number;
    imageLink?: string;
    externalLink?: string;
  }): Promise<boolean> => {
    const topics =
      data.userType === UserType.Customer
        ? ['customers']
        : data.userType === UserType.Provider
          ? ['providers']
          : ['all'];
    for (const topic of topics) {
      await apiClient.post<boolean>('/adminnotifications/broadcast', {
        title: data.title,
        message: data.message,
        topic,
        imageLink: data.imageLink,
        externalLink: data.externalLink,
        notificationMethodType: NotificationMethodType.Push,
        notificationTypeId: NotificationType.Cast,
        parentId: 0,
      });
    }
    return true;
  },

  // Send a manual notification to one user. Backend NotificationInputModel
  // wire names: message (not body) + userType ('customer'/'provider', used
  // to resolve the target's mobile) + notificationMethodType; the route
  // lives on /adminnotifications/manual.
  sendManual: async (data: {
    userId: string;
    title: string;
    message: string;
    userType: 'customer' | 'provider';
  }): Promise<boolean> => {
    const response = await apiClient.post<boolean>('/adminnotifications/manual', {
      userId: data.userId,
      title: data.title,
      message: data.message,
      userType: data.userType,
      notificationMethodType: NotificationMethodType.Push,
    });
    return response.data;
  },

  // Personal notifications of a user — GET /adminnotifications?userId=
  // (there is no /supports/notifications/user/{id} route).
  getByUser: async (userId: string, page: number): Promise<Notification[]> => {
    const params = buildQueryParams({ userId, page });
    const response = await apiClient.get<Notification[]>(
      `/adminnotifications${params}`
    );
    return response.data;
  },

  // Get customers who read a broadcast notification
  getReadingCustomers: async (notificationId: number, page: number): Promise<Customer[]> => {
    const params = buildQueryParams({ notificationId, page });
    const response = await apiClient.get<Customer[]>(
      `/adminnotifications/broadcast/reading/customers${params}`
    );
    return response.data;
  },

  // Get providers who read a broadcast notification
  getReadingProviders: async (notificationId: number, page: number): Promise<Provider[]> => {
    const params = buildQueryParams({ notificationId, page });
    const response = await apiClient.get<Provider[]>(
      `/adminnotifications/broadcast/reading/providers${params}`
    );
    return response.data;
  },
};

// Violations & Penalties API
export const violationsApi = {
  // Get user violations
  getByUser: async (userId: string, page: number): Promise<Violation[]> => {
    const params = buildQueryParams({ userId, page });
    const response = await apiClient.get<Violation[]>(`/supports/violations${params}`);
    return response.data;
  },

  // Add violation to user
  add: async (data: {
    userId: string;
    violationTypeId: number;
    penaltyTypeId: number;
    note?: string;
    userType: UserType;
  }): Promise<boolean> => {
    const response = await apiClient.post<boolean>('/supports/violations', data);
    return response.data;
  },

  // Get penalties by user
  getPenalties: async (userId: string, page: number): Promise<Penalty[]> => {
    const params = buildQueryParams({ userId, page });
    const response = await apiClient.get<Penalty[]>(`/supports/penalties${params}`);
    return response.data;
  },

  // Suspend user
  suspend: async (data: {
    userId: string;
    suspensionDays: number;
    reason: string;
    userType: UserType;
  }): Promise<boolean> => {
    const response = await apiClient.post<boolean>('/supports/users/suspend', data);
    return response.data;
  },

  // Remove suspension
  removeSuspension: async (userId: string): Promise<boolean> => {
    const response = await apiClient.post<boolean>(`/supports/users/${userId}/unsuspend`);
    return response.data;
  },
};

// Tickets API (from v1)
function mapLegacyTicketUserType(role?: string): UserType {
  switch ((role || '').toLowerCase()) {
    case 'provider':
      return UserType.Provider;
    case 'support':
      return UserType.Support;
    case 'customer':
    default:
      return UserType.Customer;
  }
}

function mapLegacyTicketStatus(isResolved?: boolean): TicketStatus {
  return isResolved ? TicketStatus.Resolved : TicketStatus.Open;
}

interface LegacyTicket extends Partial<Ticket> {
  id: number;
  userId: string;
  createdOn: string;
  userFullName?: string;
  userProfileLogo?: string;
  role?: string;
  ticketCategoryId?: number;
  ticketCategoryArabic?: string;
  ticketSubCategoryId?: number;
  ticketSubCategoryArabic?: string;
  title?: string;
  isResolved?: boolean;
  actionOn?: string;
  actionByUserId?: string;
  lastTicketMessageDate?: string;
}

interface LegacyTicketMessage extends Partial<TicketMessage> {
  id: number;
  ticketId: number;
  userId: string;
  userTypeEnum: UserType;
  messageType: TicketMessage['messageType'];
  body: string;
  createdOn: string;
  userFullName?: string;
  userProfileLogo?: string;
}

function mapLegacyTicket(ticket: LegacyTicket): Ticket {
  const statusId = typeof ticket.statusId === 'number'
    ? ticket.statusId
    : mapLegacyTicketStatus(ticket.isResolved);

  return {
    id: ticket.id,
    userId: ticket.userId,
    userName: ticket.userName || ticket.userFullName || '',
    userProfileImage: ticket.userProfileImage || ticket.userProfileLogo || '',
    userTypeEnum: typeof ticket.userTypeEnum === 'number'
      ? ticket.userTypeEnum
      : mapLegacyTicketUserType(ticket.role),
    categoryId: ticket.categoryId ?? ticket.ticketCategoryId ?? 0,
    categoryName: ticket.categoryName || ticket.ticketCategoryArabic || '',
    subCategoryId: ticket.subCategoryId ?? ticket.ticketSubCategoryId,
    subCategoryName: ticket.subCategoryName || ticket.ticketSubCategoryArabic || '',
    subject: ticket.subject || ticket.title || '',
    statusId,
    statusName: ticket.statusName || (statusId === TicketStatus.Resolved ? 'Resolved' : 'Open'),
    createdOn: ticket.createdOn,
    resolvedOn: ticket.resolvedOn || ticket.actionOn,
    resolvedByUserId: ticket.resolvedByUserId || ticket.actionByUserId,
    currentAgentUserId: ticket.currentAgentUserId,
    currentAgentName: ticket.currentAgentName,
    currentAgentPhoto: ticket.currentAgentPhoto,
    isLiveSession: ticket.isLiveSession,
    priority: ticket.priority,
    lastMessageAt: ticket.lastMessageAt || ticket.lastTicketMessageDate,
    hasUnreadAgentMessages: ticket.hasUnreadAgentMessages,
    firstResponseAt: ticket.firstResponseAt,
    slaResponseMinutes: ticket.slaResponseMinutes,
    slaResolutionMinutes: ticket.slaResolutionMinutes,
    isSlaBreach: ticket.isSlaBreach,
  };
}

function mapLegacyTicketMessage(message: LegacyTicketMessage): TicketMessage {
  return {
    id: message.id,
    ticketId: message.ticketId,
    userId: message.userId,
    userName: message.userName || message.userFullName || '',
    userProfileImage: message.userProfileImage || message.userProfileLogo || '',
    userTypeEnum: message.userTypeEnum,
    messageType: message.messageType,
    body: message.body,
    filePath: message.filePath,
    createdOn: message.createdOn,
  };
}

function mapStatusFilter(statusId: number): TicketInputStatus {
  switch (statusId) {
    case TicketStatus.Resolved:
    case TicketStatus.Closed:
      return TicketInputStatus.Closed;
    case 0:
      return TicketInputStatus.All;
    default:
      return TicketInputStatus.Open;
  }
}

export interface ManagedTicketSubCategory {
  id: number;
  arabicName: string | null;
  englishName: string | null;
  isActive: boolean;
}

export interface ManagedTicketCategory {
  id: number;
  arabicName: string | null;
  englishName: string | null;
  userTypeEnum: number;
  isActive: boolean;
  subCategories: ManagedTicketSubCategory[];
}

export const ticketsApi = {
  getBySupport: async (
    userId: string,
    ticketUserType: number,
    ticketInputStatus: number,
    page: number
  ): Promise<Ticket[]> => {
    const params = buildQueryParams({
      userId,
      ticketUserType,
      ticketInputStatus: mapStatusFilter(ticketInputStatus),
      page,
    });
    const response = await apiClient.get<LegacyTicket[]>(`/tickets/support${params}`);
    return response.data.map(mapLegacyTicket);
  },

  getUserTickets: async (
    userId: string,
    ticketInputStatus: number,
    page: number
  ): Promise<Ticket[]> => {
    const params = buildQueryParams({ ticketInputStatus, page });
    const response = await apiClient.get<LegacyTicket[]>(`/tickets/support/users/${userId}${params}`);
    return response.data.map(mapLegacyTicket);
  },

  getMessages: async (ticketId: number, page: number): Promise<TicketMessage[]> => {
    const params = buildQueryParams({ page });
    const response = await apiClient.get<LegacyTicketMessage[]>(
      `/tickets/support/chat/${ticketId}${params}`
    );
    return response.data.map(mapLegacyTicketMessage);
  },

  addMessage: async (
    ticketId: number,
    data: { body: string; messageType: number }
  ): Promise<TicketMessage> => {
    const response = await apiClient.post<LegacyTicketMessage>(
      `/tickets/support/chat/${ticketId}`,
      data
    );
    return mapLegacyTicketMessage(response.data);
  },

  resolve: async (data: {
    ticketId: number;
    resolutionNote: string;
  }): Promise<boolean> => {
    const response = await apiClient.post<boolean>('/tickets/support/resolve', {
      id: data.ticketId,
      isResolved: true,
      actionComment: data.resolutionNote,
    });
    return response.data;
  },

  updateStatus: async (ticketId: number, statusId: number): Promise<boolean> => {
    const response = await apiClient.post<boolean>(`/tickets/support/${ticketId}/status`, null, {
      params: { statusId },
    });
    return response.data;
  },

  getById: async (ticketId: number): Promise<Ticket> => {
    const response = await apiClient.get<LegacyTicket>(`/tickets/support/${ticketId}`);
    return mapLegacyTicket(response.data);
  },

  // Create new ticket (support creates for user)
  create: async (data: {
    userId: string;
    userType: number;
    categoryId: number;
    subCategoryId: number;
    subject: string;
    body: string;
  }): Promise<Ticket> => {
    const response = await apiClient.post<LegacyTicket>('/tickets/support', {
      userId: data.userId,
      userTypeEnum: data.userType,
      ticketCategoryId: data.categoryId,
      ticketSubCategoryId: data.subCategoryId,
      title: data.subject,
      body: data.body,
    });
    return mapLegacyTicket(response.data);
  },

  // Get ticket categories
  getCategories: async (userTypeEnum: number): Promise<TicketCategory[]> => {
    const params = buildQueryParams({ userTypeEnum });
    const response = await apiClient.get<TicketCategory[]>(`/tickets/ticket_categories${params}`);
    return response.data;
  },

  // Get ticket subcategories
  getSubCategories: async (ticketCategoryId: number): Promise<TicketSubCategory[]> => {
    const params = buildQueryParams({ ticketCategoryId });
    const response = await apiClient.get<TicketSubCategory[]>(
      `/tickets/ticket_subcategories${params}`
    );
    return response.data;
  },

  // ── Admin: per-tenant category management ("ticket questions") ──
  getManagedCategories: async (userTypeEnum: number): Promise<ManagedTicketCategory[]> => {
    const params = buildQueryParams({ userTypeEnum });
    const response = await apiClient.get<ManagedTicketCategory[]>(`/ticket-categories/manage${params}`);
    return response.data;
  },
  createCategory: async (data: { arabicName: string; englishName: string; userTypeEnum: number }): Promise<{ id: number }> => {
    const response = await apiClient.post<{ id: number }>('/ticket-categories', data);
    return response.data;
  },
  updateCategory: async (id: number, data: { arabicName?: string; englishName?: string; isActive?: boolean }): Promise<void> => {
    await apiClient.put(`/ticket-categories/${id}`, data);
  },
  createSubCategory: async (categoryId: number, data: { arabicName: string; englishName: string }): Promise<{ id: number }> => {
    const response = await apiClient.post<{ id: number }>(`/ticket-categories/${categoryId}/subcategories`, data);
    return response.data;
  },
  updateSubCategory: async (id: number, data: { arabicName?: string; englishName?: string; isActive?: boolean }): Promise<void> => {
    await apiClient.put(`/ticket-categories/subcategories/${id}`, data);
  },

  // Set ticket priority
  setPriority: async (ticketId: number, priorityId: number): Promise<boolean> => {
    const response = await apiClient.post<boolean>(
      `/tickets/support/${ticketId}/priority`,
      { priorityId }
    );
    return response.data;
  },

  // Assign ticket to agent
  assignAgent: async (
    ticketId: number,
    agentUserId: string,
    agentName?: string
  ): Promise<boolean> => {
    const response = await apiClient.post<boolean>(
      `/tickets/support/${ticketId}/assign`,
      { agentUserId, agentName }
    );
    return response.data;
  },
};

// Addresses API
export const addressesApi = {
  getAll: async (userId: string, userTypeEnum: UserType): Promise<Address[]> => {
    const params = buildQueryParams({ userId, userTypeEnum });
    const response = await apiClient.get<Address[]>(`/supports/address${params}`);
    return response.data;
  },

  add: async (data: Partial<Address>): Promise<number> => {
    const response = await apiClient.post<number>('/supports/address', data);
    return response.data;
  },

  delete: async (userId: string, addressId: number): Promise<boolean> => {
    const response = await apiClient.delete<boolean>(`/supports/address/${addressId}`, {
      params: { userId },
    });
    return response.data;
  },
};

// Tenants Admin API
// SuperAdmin-only cross-tenant analytics. Routed through /admin/* so the
// gateway's AdminOnly policy gates it.
export interface PlatformGmvTenantRow {
  tenantId: number;
  tenantName?: string;
  identifier?: string;
  gmv: number;
  orders: number;
}

export interface PlatformGmvData {
  totalGmv: number;
  serviceOrdersGmv: number;
  shopOrdersGmv: number;
  totalCompletedOrders: number;
  totalDeliveredShopOrders: number;
  activeTenantsInWindow: number;
  byTenant: PlatformGmvTenantRow[];
  moduleUsage: Record<string, number>;
}

export const platformAnalyticsApi = {
  getGmv: async (from?: string, to?: string): Promise<PlatformGmvData> => {
    const qs: string[] = [];
    if (from) qs.push(`from=${encodeURIComponent(from)}`);
    if (to) qs.push(`to=${encodeURIComponent(to)}`);
    const path = `/admin/platform-analytics/gmv${qs.length ? `?${qs.join('&')}` : ''}`;
    const response = await apiClient.get<PlatformGmvData>(path);
    return response.data;
  },
};

export interface TenantFirebaseAppView {
  platform: 'android' | 'ios' | 'web';
  bundleIdOrName: string;
  registered: boolean;
  firebaseAppId?: string | null;
  registeredAt?: string | null;
  lastSyncedAt?: string | null;
}

export interface TenantFirebaseStatus {
  tenantId: number;
  identifier: string;
  firebaseProjectId: string;
  configured: number;
  expected: number;
  summary: 'NotConfigured' | 'Partial' | 'Configured' | 'Error';
  apps: TenantFirebaseAppView[];
}

export const platformFirebaseApi = {
  resyncAll: async (): Promise<{ tenantsProcessed: number; results: TenantFirebaseStatus[] }> => {
    const response = await apiClient.post<{ tenantsProcessed: number; results: TenantFirebaseStatus[] }>(
      '/admin/platform/firebase/resync'
    );
    return response.data;
  },
};

export const tenantsApi = {
  getAll: async (): Promise<TenantListItem[]> => {
    const response = await apiClient.get<TenantListItem[]>('/admin/tenants');
    return response.data;
  },

  getById: async (id: number): Promise<Tenant> => {
    const response = await apiClient.get<Tenant>(`/admin/tenants/${id}`);
    return response.data;
  },

  getByIdentifier: async (identifier: string): Promise<Tenant> => {
    const response = await apiClient.get<Tenant>(`/admin/tenants/by-identifier/${identifier}`);
    return response.data;
  },

  create: async (data: CreateTenantRequest): Promise<{
    tenant: Tenant;
    tenantAdminUserId?: string;
    tenantAdminEmail?: string;
    subscriptionCreated?: boolean;
    warnings: string[];
  }> => {
    const response = await apiClient.post<Tenant | {
      tenant: Tenant;
      tenantAdminUserId?: string;
      tenantAdminEmail?: string;
      subscriptionCreated?: boolean;
      warnings?: string[];
    }>('/admin/tenants', data);
    if ('tenant' in response.data) {
      return { ...response.data, warnings: response.data.warnings ?? [] };
    }
    return { tenant: response.data, warnings: [] };
  },

  update: async (id: number, data: UpdateTenantRequest): Promise<Tenant> => {
    const response = await apiClient.put<Tenant>(`/admin/tenants/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/admin/tenants/${id}`);
  },

  getModules: async (tenantId: number): Promise<TenantModule[]> => {
    const response = await apiClient.get<TenantModule[]>(`/admin/tenants/${tenantId}/modules`);
    return response.data;
  },

  setModules: async (tenantId: number, data: SetTenantModulesRequest): Promise<TenantModule[]> => {
    const response = await apiClient.put<TenantModule[]>(`/admin/tenants/${tenantId}/modules`, data);
    return response.data;
  },

  toggleModule: async (tenantId: number, moduleKey: string, enabled: boolean): Promise<void> => {
    await apiClient.patch(`/admin/tenants/${tenantId}/modules/${moduleKey}?enabled=${enabled}`);
  },

  getAvailableModules: async (): Promise<AvailableModule[]> => {
    const response = await apiClient.get<AvailableModule[]>('/admin/tenants/available-modules');
    return response.data;
  },

  /**
   * Read the per-tenant Firebase app registration state.
   */
  getFirebaseStatus: async (id: number): Promise<TenantFirebaseStatus> => {
    const response = await apiClient.get<TenantFirebaseStatus>(`/admin/tenants/${id}/firebase/status`);
    return response.data;
  },

  /**
   * Register any missing Firebase apps for this tenant under the shared
   * project and refresh cached configs. Idempotent.
   */
  configureFirebase: async (id: number): Promise<TenantFirebaseStatus> => {
    const response = await apiClient.post<TenantFirebaseStatus>(`/admin/tenants/${id}/firebase/configure`);
    return response.data;
  },

  /**
   * Download a "mobile pack" zip for this tenant — a populated tenant.json
   * + per-app README files consumed by tools/Add-Tenant.ps1 in the repo.
   * See docs/add-tenant-runbook.md for the end-to-end flow.
   */
  downloadMobilePack: async (id: number, identifier: string): Promise<void> => {
    const response = await apiClient.get(`/admin/tenants/${id}/mobile-pack`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data as BlobPart], { type: 'application/zip' });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${identifier}-mobile-pack.zip`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);
  },
};

// Current-tenant metadata (public bootstrap endpoint). Resolves the tenant
// from the request context (X-Tenant-Host / X-Impersonate-Tenant), so it
// reflects whichever tenant the dashboard is acting as. Used to gate the
// tenant-admin UI by enabled modules.
export const tenantMetaApi = {
  getMeta: async (): Promise<CurrentTenantMeta> => {
    const response = await apiClient.get<CurrentTenantMeta>('/tenants/meta');
    return response.data;
  },
};

// App Versions API
export const appVersionsApi = {
  getAll: async (): Promise<AppVersion[]> => {
    const response = await apiClient.get<AppVersion[]>('/lookups/appversions');
    return response.data;
  },

  getById: async (id: number): Promise<AppVersion> => {
    const response = await apiClient.get<AppVersion>(`/lookups/appversion/${id}`);
    return response.data;
  },

  update: async (id: number, data: UpdateAppVersionRequest): Promise<{ success: boolean }> => {
    const response = await apiClient.put<{ success: boolean }>(`/lookups/appversion/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<{ success: boolean }> => {
    const response = await apiClient.delete<{ success: boolean }>(`/lookups/appversion/${id}`);
    return response.data;
  },
};

// Branding API
export const brandingApi = {
  get: async (): Promise<TenantBranding> => {
    const response = await apiClient.get<TenantBranding>('/branding');
    return response.data;
  },

  updatePlatform: async (platform: string, data: PlatformBranding): Promise<TenantBranding> => {
    const response = await apiClient.put<TenantBranding>(`/branding/${platform}`, data);
    return response.data;
  },

  updateAll: async (data: TenantBranding): Promise<TenantBranding> => {
    const response = await apiClient.put<TenantBranding>('/branding', data);
    return response.data;
  },
};

// Sliders Admin API
export const slidersApi = {
  getAll: async (userType?: number): Promise<SliderItem[]> => {
    const params = userType !== undefined ? buildQueryParams({ userType }) : '';
    const response = await apiClient.get<SliderItem[]>(`/admin/sliders${params}`);
    return response.data;
  },

  create: async (data: CreateSliderRequest): Promise<SliderItem> => {
    const response = await apiClient.post<SliderItem>('/admin/sliders', data);
    return response.data;
  },

  update: async (id: number, data: UpdateSliderRequest): Promise<SliderItem> => {
    const response = await apiClient.put<SliderItem>(`/admin/sliders/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/admin/sliders/${id}`);
  },
};

// FAQs API
export const faqsApi = {
  getTypes: async (userTypeEnum: number): Promise<FaqType[]> => {
    const params = buildQueryParams({ userTypeEnum });
    const response = await apiClient.get<FaqType[]>(`/faqs/types${params}`);
    return response.data;
  },

  getAll: async (faqTypeId: number, page: number): Promise<Faq[]> => {
    const params = buildQueryParams({ faqTypeId, page });
    const response = await apiClient.get<Faq[]>(`/faqs${params}`);
    return response.data;
  },

  getById: async (faqId: number): Promise<Faq> => {
    const response = await apiClient.get<Faq>(`/faqs/${faqId}`);
    return response.data;
  },

  add: async (data: Partial<Faq>): Promise<Faq> => {
    const response = await apiClient.post<Faq>('/faqs', data);
    return response.data;
  },

  like: async (faqId: number): Promise<boolean> => {
    const response = await apiClient.post<boolean>(`/faqs/${faqId}/like`);
    return response.data;
  },

  dislike: async (faqId: number): Promise<boolean> => {
    const response = await apiClient.post<boolean>(`/faqs/${faqId}/dislike`);
    return response.data;
  },

  incrementViewCount: async (faqId: number): Promise<boolean> => {
    const response = await apiClient.post<boolean>(`/faqs/${faqId}/viewcount`);
    return response.data;
  },
};

// File Upload API
export const uploadFile = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);
  // Upload through Gateway so tenantId/userId are derived from the trusted
  // authenticated context. Storage's raw endpoint has a different route and
  // requires caller-supplied ownership identifiers.
  const response = await apiClient.post<{ filePath: string; fullUrl: string }>('/uploads', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data.fullUrl || response.data.filePath;
};

// Products API (Shop module) - Routes go to Maksab.Core at /products/supports/...
export const productsApi = {
  getAll: async (
    specialtyId?: number,
    providerId?: number,
    page?: number
  ): Promise<Product[]> => {
    const params = buildQueryParams({ specialtyId, providerId, page });
    const response = await apiClient.get<Product[]>(`/products/supports${params}`);
    return response.data;
  },

  getByProvider: async (userId: string, specialtyId: number, page?: number): Promise<Product[]> => {
    const params = buildQueryParams({ page });
    const response = await apiClient.get<Product[]>(
      `/products/providers/${userId}/specialties/${specialtyId}${params}`
    );
    return response.data;
  },

  // Tenant-wide counts for the dashboard stat cards (whole catalog, not a page)
  getSummary: async (specialtyId?: number): Promise<ProductsSummary> => {
    const params = buildQueryParams({ specialtyId });
    const response = await apiClient.get<ProductsSummary>(`/products/supports/summary${params}`);
    return response.data;
  },

  getById: async (productId: number): Promise<Product> => {
    const response = await apiClient.get<Product>(`/products/${productId}`);
    return response.data;
  },

  create: async (data: ProductInput): Promise<number> => {
    const response = await apiClient.post<number>('/products/supports', data);
    return response.data;
  },

  update: async (productId: number, data: Partial<ProductInput>): Promise<boolean> => {
    const response = await apiClient.put<boolean>(`/products/supports/${productId}`, data);
    return response.data;
  },

  toggleStatus: async (productId: number): Promise<boolean> => {
    const response = await apiClient.post<boolean>(`/products/supports/${productId}/toggle`);
    return response.data;
  },

  delete: async (productId: number, userId: string): Promise<boolean> => {
    const response = await apiClient.delete<boolean>(`/products/supports/${productId}`, {
      params: { userId },
    });
    return response.data;
  },

  updateStock: async (product: Product, stockQuantity: number): Promise<boolean> =>
    productsApi.update(product.id, {
      specialtyId: product.specialtyId,
      subSpecialtyId: product.subSpecialtyId,
      providerUserId: product.providerUserId,
      providerId: product.providerId,
      name: product.name,
      description: product.description,
      price: product.price,
      imagePath: product.imagePath,
      stockQuantity,
      isActive: product.isActive,
    }),
};

// Booking Slots API (Booking module) - Routes go to Maksab.Core at /booking-slots/...
export const bookingSlotsApi = {
  getByProvider: async (userId: string, specialtyId?: number): Promise<BookingSlot[]> => {
    const params = buildQueryParams({ specialtyId });
    const response = await apiClient.get<BookingSlot[]>(
      `/booking-slots/supports/providers/${userId}/slots${params}`
    );
    return response.data;
  },

  create: async (data: BookingSlotInput): Promise<number> => {
    const response = await apiClient.post<number>('/booking-slots/supports', data);
    return response.data;
  },

  update: async (slotId: number, data: BookingSlotInput): Promise<boolean> => {
    const response = await apiClient.put<boolean>(`/booking-slots/supports/${slotId}`, data);
    return response.data;
  },

  toggleStatus: async (slotId: number): Promise<boolean> => {
    const response = await apiClient.post<boolean>(`/booking-slots/supports/${slotId}/toggle`);
    return response.data;
  },

  delete: async (slotId: number, userId: string): Promise<boolean> => {
    const response = await apiClient.delete<boolean>(`/booking-slots/supports/${slotId}`, {
      params: { userId },
    });
    return response.data;
  },
};

// Maps API
export const mapsApi = {
  getNearbyOrders: async (lat: number, lng: number, radius: number, statusId?: number): Promise<NearbyOrder[]> => {
    const params = buildQueryParams({ latitude: lat, longitude: lng, radius, statusId });
    const response = await apiClient.get<NearbyOrder[]>(`/supports/nearby/orders${params}`);
    return response.data;
  },
  getNearbyProviders: async (lat: number, lng: number, radius: number, specialtyId?: number): Promise<NearbyProvider[]> => {
    const params = buildQueryParams({ latitude: lat, longitude: lng, radius, specialtyId });
    const response = await apiClient.get<NearbyProvider[]>(`/supports/nearby/providers${params}`);
    return response.data;
  },
};
